import { artworkCmd } from '../services/apiClient';
import { readPutAwayRows, readRow, readRows, readSummaries, type FileRow, type ProjectSummary, type PutAwayRow, type Scope } from './rows';
import type { StartSize } from './sizes';

/**
 * The home's calls, through `artworkCmd`. Projects themselves (names, cards, the picker) are the
 * shared kit's, read through `projects/projectsApi.ts`.
 */

export interface ListBody {
  readonly limit: number;
  readonly skip?: number;
  readonly search?: string;
  /** Omitted: every file, as a search or a project's grid asks. */
  readonly scope?: Scope;
  /** Omitted: any project. null: not in one. */
  readonly projectId?: string | null;
}

export async function listFiles(body: ListBody): Promise<FileRow[]> {
  return readRows(await artworkCmd<unknown>('artwork.doc.list', { ...body }));
}

/** The projects that hold at least one Artwork file the caller can open, with counts and pictures. */
export async function listProjectSummaries(): Promise<ProjectSummary[]> {
  return readSummaries(await artworkCmd<unknown>('artwork.doc.projects', {}));
}

export async function moveFile(documentId: string, projectId: string | null): Promise<FileRow | null> {
  return readRow(await artworkCmd<unknown>('artwork.doc.move', { documentId, projectId }));
}

/**
 * A new file: in a project when one is named, starting on a board of `artboard`'s size, made
 * in `brandId`'s brand. Every part is optional; with none it is the blank "Untitled" it was.
 */
export async function createFile(start: { projectId?: string | null; name?: string; artboard?: StartSize; brandId?: string } = {}): Promise<{ id: string; name: string }> {
  const file = await artworkCmd<{ id: string; name?: string }>('artwork.doc.create', {
    name: start.name ?? 'Untitled',
    ...(start.projectId ? { projectId: start.projectId } : {}),
    ...(start.artboard ? { artboard: start.artboard } : {}),
    ...(start.brandId ? { brandId: start.brandId } : {}),
  });
  return { id: file.id, name: file.name ?? start.name ?? 'Untitled' };
}

/** A copy of the whole file, mine, named "… copy" (ART-UX-06). */
export async function duplicateFile(documentId: string): Promise<{ id: string; name: string }> {
  const file = await artworkCmd<{ id: string; name?: string }>('artwork.doc.duplicate', { documentId });
  return { id: file.id, name: file.name ?? 'Untitled copy' };
}

/** Into the Trash: out of every list, deleted for good after 30 days unless it is restored. */
export async function deleteFile(documentId: string): Promise<void> {
  await artworkCmd('artwork.doc.delete', { documentId });
}

/** My files in the Trash, deleted last first. */
export async function listTrashed(body: Pick<ListBody, 'limit' | 'skip'>): Promise<PutAwayRow[]> {
  return readPutAwayRows(await artworkCmd<unknown>('artwork.doc.listTrashed', { limit: body.limit, skip: body.skip }));
}

/** My archived files, archived last first. */
export async function listArchived(body: Pick<ListBody, 'limit' | 'skip'>): Promise<PutAwayRow[]> {
  return readPutAwayRows(await artworkCmd<unknown>('artwork.doc.listArchived', { limit: body.limit, skip: body.skip }));
}

/** Out of the Trash, back where it was. */
export async function restoreFile(documentId: string): Promise<void> {
  await artworkCmd('artwork.doc.restore', { documentId });
}

/** A file in the Trash, deleted for good. */
export async function deleteFileForever(documentId: string): Promise<void> {
  await artworkCmd('artwork.doc.deleteForever', { documentId });
}

/** Archive a file I made (out of every list but Archived), or bring it back. */
export async function archiveFile(documentId: string, archived: boolean): Promise<void> {
  await artworkCmd('artwork.doc.archive', { documentId, archived });
}
